"use client";

import { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScaleYourAgencyWithoutRisk() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const carriers = [{
    name: "Mutual of Omaha",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_mutual.png"
  }, {
    name: "Aetna",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_aetna.png"
  }, {
    name: "Foresters Financial",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_foresters.png"
  }, {
    name: "Americo",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_americo.png"
  }, {
    name: "Transamerica",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_transamerica.png"
  }, {
    name: "Prosperity Life",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_prosperity.png"
  }, {
    name: "Gerber Life",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_gerber.png"
  }, {
    name: "Royal Neighbors of America",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_royal_neighbors.png"
  }, {
    name: "Trinity Life Insurance Company",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_trinity.png"
  }, {
    name: "American Amicable",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_american_amicable.png"
  }, {
    name: "Great Western Insurance Company",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_great_western.png"
  }, {
    name: "Fidelity Life",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_fidelity.png"
  }, {
    name: "SBLI",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_sbli.png"
  }, {
    name: "Baltimore Life",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_baltimore.png"
  }, {
    name: "Liberty Bankers Life",
    logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/carrier_logo_liberty_bankers.png"
  }];
  const [currentCarrierIndex, setCurrentCarrierIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCarrierIndex(prev => (prev + 1) % carriers.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [carriers.length]);
  const nextCarrier = () => {
    setCurrentCarrierIndex(prev => (prev + 1) % carriers.length);
  };
  const prevCarrier = () => {
    setCurrentCarrierIndex(prev => (prev - 1 + carriers.length) % carriers.length);
  };
  return <section data-section-id="4938" id="scale-your-agency-without-risk" className="dark relative min-h-screen w-full overflow-hidden bg-background text-foreground" style={{
    backgroundImage: "radial-gradient(circle at 18% 28%, hsla(220, 60%, 30%, 0.35) 0%, transparent 55%), radial-gradient(circle at 82% 65%, hsla(45, 85%, 55%, 0.08) 0%, transparent 50%), radial-gradient(circle at 50% 50%, hsla(220, 20%, 4%, 1) 0%, hsla(220, 20%, 4%, 1) 100%)"
  }}>
      {/* Noise Texture Overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      {/* Vertical Hairline Divider */}
      <div className="pointer-events-none absolute top-0 bottom-0 hidden lg:block" style={{
      left: "58%",
      width: "1px",
      background: "linear-gradient(to bottom, transparent, var(--border), transparent)",
      opacity: 0.35
    }} />

      <div className="relative mx-auto flex min-h-screen max-w-[1600px] flex-col px-6 pt-10 pb-28 sm:px-10 lg:px-16">
        <div className="flex flex-1 flex-col gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-14">
          <div ref={leftRef} className={`flex flex-col justify-center pr-0 transition-all duration-1000 ease-out lg:max-w-[58%] lg:pr-8 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Eyebrow Label */}
            <div className="mb-4 flex items-center gap-3.5">
              <div className="h-px w-10 shrink-0" style={{
              background: "var(--accent)"
            }} />
              <span className="font-default text-[11px] font-semibold uppercase tracking-[0.24em]" style={{
              color: "var(--accent)"
            }}>
                RMA SOLUTIONS · PERFORMANCE ARCHITECTURE
              </span>
            </div>

            {/* Carrier Showcase Card */}
            <div className="mb-6 sm:mb-7 inline-flex flex-col gap-2.5 rounded-lg border border-border/70 bg-card/45 p-2.5 sm:p-3.5 backdrop-blur-xl shadow-xl shadow-black/25 ring-1 ring-border/40 max-w-fit">
              <div className="flex items-center gap-2.5 sm:gap-3.5">
                <Button variant="ghost" size="icon" onClick={prevCarrier} aria-label="Previous carrier" className="size-8 rounded-lg border border-border/70 bg-background/60 text-foreground/70 transition-all duration-200 hover:border-accent/60 hover:bg-card hover:text-foreground hover:shadow-xs">
                  <ChevronLeft className="size-4" />
                </Button>

                <div className="relative flex min-w-[250px] sm:min-w-[280px] items-center gap-3 overflow-hidden py-0.5">
                  <div key={currentCarrierIndex} className="flex items-center gap-3 transition-all duration-500 ease-out animate-in fade-in slide-in-from-right-3">
                    <div className="flex size-11 items-center justify-center overflow-hidden rounded-lg border border-border/80 bg-card p-1.5 shadow-xs">
                      <img src={carriers[currentCarrierIndex].logo} alt={carriers[currentCarrierIndex].name} className="size-8 object-contain transition-transform duration-300 hover:scale-105" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-default text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/55">
                        Featured Carrier
                      </span>
                      <span className="font-default text-xs sm:text-sm font-bold tracking-wider text-accent uppercase">
                        {carriers[currentCarrierIndex].name}
                      </span>
                    </div>
                  </div>
                </div>

                <Button variant="ghost" size="icon" onClick={nextCarrier} aria-label="Next carrier" className="size-8 rounded-lg border border-border/70 bg-background/60 text-foreground/70 transition-all duration-200 hover:border-accent/60 hover:bg-card hover:text-foreground hover:shadow-xs">
                  <ChevronRight className="size-4" />
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-1.5 pt-0.5">
                {carriers.map((_, idx) => <div key={idx} data-index={idx} className={`h-1 rounded-lg transition-all duration-300 ${idx === currentCarrierIndex ? "w-5 bg-accent shadow-xs" : "w-1.5 bg-border/70 hover:bg-border"}`} />)}
              </div>
            </div>

            {/* Headline */}
            <h1 className="font-default font-black uppercase text-transparent bg-clip-text bg-[linear-gradient(135deg,var(--foreground)_40%,var(--accent)_100%)]" style={{
            fontSize: "clamp(2rem, 4.2vw, 3.6rem)",
            letterSpacing: "-0.03em",
            lineHeight: "1.08",
            fontWeight: 900
          }}>
              RMA Solutions LLC — Business Support, Digital Services & Insurance Lead Generation
            </h1>

            {/* Supporting Copy */}
            <div className="mt-6 mb-3 max-w-[620px] space-y-3">
              <p className="font-default text-base sm:text-lg font-semibold tracking-tight text-accent flex items-center">
                Performance-Based Partner for Insurance Growth
                <ArrowRight className="ml-2 inline-block size-5 shrink-0 transition-transform duration-300 hover:translate-x-1" />
              </p>
              <p className="font-default text-sm sm:text-base font-normal leading-relaxed tracking-normal text-foreground/80">
                RMA Solutions LLC is a professional telemarketing, lead generation, digital marketing, and outsourcing company helping insurance agents grow their business. We empower agents with qualified leads, appointment setting, customer outreach, and dedicated sales support at zero upfront cost through our high-converting performance-based partnership model.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col items-stretch gap-3.5 sm:flex-row sm:flex-wrap sm:items-center">
              {/* Primary CTA - Gold Accent */}
              <Button asChild size="lg" className="group h-14 sm:h-16 bg-accent text-accent-foreground font-default text-sm sm:text-base font-bold uppercase tracking-[0.16em] shadow-2xl shadow-accent/30 ring-2 ring-accent/40 transition-all duration-300 hover:bg-accent/90 hover:shadow-2xl hover:shadow-accent/50 hover:ring-accent/70 hover:-translate-y-0.5 rounded-md px-8">
                <Link to="/GetStartedPage">
                  GET STARTED
                  <ArrowRight className="ml-2.5 size-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
              </Button>

              {/* Secondary CTA - Partner Program */}
              <Button asChild size="lg" variant="outline" className="h-14 sm:h-16 border border-border/80 bg-card/40 px-7 font-default text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-foreground/90 backdrop-blur-sm transition-all duration-300 hover:border-accent/60 hover:bg-card/80 hover:text-foreground rounded-md">
                <Link to="/PartnerProgramPage">
                  PARTNER WITH US
                </Link>
              </Button>

              {/* Outline CTA - Phone Link */}
              <Button asChild size="lg" variant="outline" className="h-14 sm:h-16 border border-border/80 bg-card/40 px-7 font-default text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-foreground/90 backdrop-blur-sm transition-all duration-300 hover:border-accent/60 hover:bg-card/80 hover:text-foreground rounded-md">
                <a href="tel:+13309341671">
                  <Phone className="mr-2 size-4 text-accent" />
                  TALK TO OUR TEAM
                </a>
              </Button>
            </div>
          </div>

          {/* Visual Column / Photo */}
          <div ref={rightRef} className={`relative flex flex-col items-center justify-center transition-all duration-1000 ease-out lg:max-w-[42%] ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* Ambient Background Glows */}
            <div className="absolute -top-6 -right-6 h-64 w-64 rounded-lg blur-3xl pointer-events-none opacity-40" style={{
            background: "var(--primary)"
          }} />
            <div className="absolute -bottom-8 -left-8 h-60 w-60 rounded-lg blur-3xl pointer-events-none opacity-25" style={{
            background: "var(--accent)"
          }} />

            <div className="relative w-full rounded-lg p-2 border border-border/80 bg-card/40 backdrop-blur-md shadow-2xl shadow-black/60 ring-1 ring-border/40">
              <div className="relative overflow-hidden rounded-lg">
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80" data-wvc-srcset="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80-wvc-srcset" data-wvc-sizes="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80-wvc-sizes" alt="Two old men and a woman - RMA Solutions" loading="lazy" className="aspect-[4/5] w-full object-cover rounded-lg transition-transform duration-700 ease-out hover:scale-[1.02]" style={{
                filter: "brightness(0.88) contrast(1.12) saturate(0.95)"
              }} />
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent pointer-events-none rounded-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="pointer-events-none absolute bottom-28 left-6 hidden flex-col items-center gap-3 sm:left-10 lg:flex">
          <span className="font-default text-[10px] font-bold uppercase tracking-[0.3em]" style={{
          color: "var(--primary)",
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          display: "inline-block"
        }}>
            SCROLL TO DISCOVER
          </span>
          <div className="relative h-16 w-px overflow-hidden bg-foreground/15">
            <div className="absolute top-0 left-0 h-4 w-full animate-[scrollLine_2s_ease-in-out_infinite]" style={{
            background: "var(--primary)"
          }} />
          </div>
          <ChevronDown className="size-3" style={{
          color: "var(--primary)"
        }} />
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-33.333%); }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
    </section>;
}